import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { Header } from "../components/header/index.tsx";
import { Seo } from "../components/seo/index.tsx";
import { Container } from "../components/styled.ts";
import { Typography } from "../components/typography/index.tsx";
import { useMobile } from "../hooks/isMobile.tsx";
import { subscriberContext } from "../context/subscriberContext.tsx";
import { API_URLS } from "../utils/apiConfig.ts";
import { StockCoverageChoice } from "../components/stockCoverage/index.tsx";
import { isTelegramChannel } from "../utils/stockCoverage.ts";


const Profile = () => {
  const history = useHistory();
  const { isMobile } = useMobile();
 

 const { subscriberDetails, plans, setSubscriberDetails, isError, isSubscriberDetailsFetching } =
   useContext(subscriberContext);
 const isLoading =
   isSubscriberDetailsFetching || !plans.length || (!subscriberDetails?.phoneNumber && !isError);

 useEffect(() => {
   if (isError) {
     history.push("/");
   }
 }, [isError, history]);
 const [confirmAction, setConfirmAction] = useState<"deactivate" | "activate" | null>(null);
 const [savingActive, setSavingActive] = useState(false);
 const [coverageSaving, setCoverageSaving] = useState(false);
 // The flag is absent for profiles that never deactivated, so only an explicit "N" pauses alerts.
 const alertsPaused = (subscriberDetails?.active || "Y").toUpperCase() === "N";

  const getStockLimit = () => {
    const plan = plans.find(p => p.planName === subscriberDetails?.subscribePlan);
    return plan ? plan.noOfStocks : (plans[0]?.noOfStocks || 7);
  };

  const saveCoverage = async (coverage: "watchlist" | "all") => {
    const token = localStorage.getItem("tokn");
    if (!token || coverageSaving) {
      return;
    }
    setCoverageSaving(true);
    try {
      const response = await fetch(API_URLS.SUBSCRIBER_COVERAGE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ coverage }),
      });
      if (!response.ok) {
        toast.error("Couldn't update which companies you follow", { autoClose: 2000 });
        return;
      }
      setSubscriberDetails(await response.json());
    } catch (error) {
      toast.error("Couldn't update which companies you follow", { autoClose: 2000 });
    } finally {
      setCoverageSaving(false);
    }
  };

  const applyActiveChange = async (activate: boolean) => {
    setSavingActive(true);
    try {
      const response = await fetch(
        activate ? API_URLS.SUBSCRIBER_ACTIVATE : API_URLS.SUBSCRIBER_DEACTIVATE,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tokn")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(
          activate ? "Could not reactivate your profile" : "Could not deactivate your profile"
        );
      }
      setSubscriberDetails(await response.json());
      setConfirmAction(null);
      toast.success(
        activate
          ? "Your profile is active again. Alerts will resume."
          : "Your profile is deactivated. You will not receive alerts.",
        { autoClose: 3000 }
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong", { autoClose: 3000 });
    } finally {
      setSavingActive(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Seo title="My Profile | FinQ India" path="/profile" noindex />
        <Header active="profile" />
        <Container
          column
          width="min(calc(100% - 40px) , 900px)"
          justifyContent="center"
          alignItems="center"
          margin="50px auto"
          gap="20px"
        >
          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize={isMobile ? "28px" : "50px"}
            lineHeight={isMobile ? "43.2px" : "60px"}
            letterSpacing={"-0.01em"}
            color="#202020"
            display="inline"
            fontWeight={500}
          >
            Loading...
          </Typography>
        </Container>
      </>
    );
  }

  if (!subscriberDetails?.phoneNumber) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Header active="profile" />
      <Container
        column
        width="min(calc(100% - 40px) , 900px)"
        justifyContent="center"
        alignItems="center"
        margin="50px auto"
        gap="20px"
      >
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "50px"}
          lineHeight={isMobile ? "43.2px" : "60px"}
          letterSpacing={"-0.01em"}
          color="#202020"
          display="inline"
          fontWeight={500}
        >
          My{" "}
          <Typography
            tag="span"
            background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
            WebkitBackgroundClip="text"
            WebkitTextFillColor="transparent"
          >
            Profile
          </Typography>
        </Typography>
      </Container>

      <Container
        column
        width="min(calc(100% - 40px) , 900px)"
        margin="0 auto"
        gap="20px"
      >
        <Container
          background="#fff"
          padding="30px"
          radius="20px"
          column
          gap="20px"
          border="2px solid #E4E4E4"
        >
          <Container column gap="10px">
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"16px"}
              fontWeight={400}
              lineHeight={"21px"}
              letterSpacing={"0.02em"}
              color="#828282"
            >
              Phone Number
            </Typography>
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"20px"}
              fontWeight={500}
              lineHeight={"30px"}
              letterSpacing={"0.02em"}
              color="#202020"
            >
              {subscriberDetails.phoneNumber}
            </Typography>
          </Container>

          <Container column gap="10px">
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"16px"}
              fontWeight={400}
              lineHeight={"21px"}
              letterSpacing={"0.02em"}
              color="#828282"
            >
              Subscription Plan
            </Typography>
            <Typography
             tag="div"
              fontFamily="Neue Montreal"
              fontSize={"20px"}
              fontWeight={500}
              lineHeight={"30px"}
              letterSpacing={"0.02em"}
              color="#202020"
            >
              {subscriberDetails.subscribePlan}
              <Typography
                tag="span"
                fontFamily="Neue Montreal"
                fontSize={"14px"}
                fontWeight={400}
                lineHeight={"21px"}
                letterSpacing={"0.02em"}
                color="#828282"
                display="block"
                margin="5px 0 0 0"
              >
                This plan allows tracking of {getStockLimit()} stocks{getStockLimit() < 100 && ", If you want to track more stocks, please upgrade your plan."}
              </Typography>
              {getStockLimit() < 100 && (
                <Container
                  onClick={() => history.push("/subscription-plans")}
                  style={{ cursor: "pointer" }}
                  margin="10px 0 0 0"
                >
                  <Typography
                    tag="span"
                    fontFamily="Neue Montreal"
                    fontSize={"14px"}
                    fontWeight={500}
                    lineHeight={"19.6px"}
                    color="#fff"
                    background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
                    borderRadius={"100px"}
                    padding={"8px 20px"}
                    display="inline-block"
                  >
                    Upgrade Now
                  </Typography>
                </Container>
              )}
            </Typography>
          </Container>

          <Container column gap="10px">
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"16px"}
              fontWeight={400}
              lineHeight={"21px"}
              letterSpacing={"0.02em"}
              color="#828282"
            >
              Subscription Term
            </Typography>
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"20px"}
              fontWeight={500}
              lineHeight={"30px"}
              letterSpacing={"0.02em"}
              color="#202020"
            >
              {subscriberDetails.subscriptionTerm}
            </Typography>
          </Container>

          <Container column gap="10px">
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"16px"}
              fontWeight={400}
              lineHeight={"21px"}
              letterSpacing={"0.02em"}
              color="#828282"
            >
              Subscription Start Date
            </Typography>
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"20px"}
              fontWeight={500}
              lineHeight={"30px"}
              letterSpacing={"0.02em"}
              color="#202020"
            >
              {formatDate(subscriberDetails.paymentDate || subscriberDetails.signupDate)}
            </Typography>
          </Container>

          <Container column gap="10px">
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"16px"}
              fontWeight={400}
              lineHeight={"21px"}
              letterSpacing={"0.02em"}
              color="#828282"
            >
              Subscription Expiry Date
            </Typography>
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"20px"}
              fontWeight={500}
              lineHeight={"30px"}
              letterSpacing={"0.02em"}
              color="#202020"
            >
              {formatDate(subscriberDetails.subscriptionExpiryDate)}
              {subscriberDetails.subscribePlan === "Life Time Free" && (
                <Typography
                  tag="span"
                  fontFamily="Neue Montreal"
                  fontSize={"14px"}
                  fontWeight={400}
                  lineHeight={"21px"}
                  letterSpacing={"0.02em"}
                  color="#828282"
                  display="block"
                  margin="5px 0 0 0"
                >
                  (Subject to company terms, this subscription will be automatically renewed monthly at no cost)
                </Typography>
              )}
            </Typography>
          </Container>
        </Container>

        {isTelegramChannel(subscriberDetails.channel) && (
          <Container
            background="#fff"
            padding={isMobile ? "20px" : "30px"}
            radius="20px"
            column
            gap="14px"
            border="2px solid #E4E4E4"
          >
            <StockCoverageChoice
              channel={subscriberDetails.channel}
              plan={subscriberDetails.subscribePlan}
              coverage={subscriberDetails.coverage}
              saving={coverageSaving}
              onChange={saveCoverage}
            />
          </Container>
        )}

        <Container
          background="#fff"
          padding="30px"
          radius="20px"
          column
          gap="14px"
          border="2px solid #E4E4E4"
          margin="0 0 50px 0"
        >
          <Container justifyContent="space-between" alignItems="center" gap="10px" wrap>
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"20px"}
              fontWeight={500}
              lineHeight={"30px"}
              letterSpacing={"0.02em"}
              color="#202020"
            >
              Stock Alerts
            </Typography>
            <StatusChip $paused={alertsPaused}>{alertsPaused ? "Paused" : "Active"}</StatusChip>
          </Container>

          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize={"14px"}
            fontWeight={400}
            lineHeight={"21px"}
            letterSpacing={"0.02em"}
            color="#828282"
          >
            {alertsPaused
              ? "Your profile is deactivated, so we are not sending any announcement alerts. Your watchlist and plan are untouched — reactivate whenever you want them back."
              : "Deactivate your profile if you do not want any announcement alerts for now. Your watchlist and plan stay as they are, and you can reactivate any time."}
          </Typography>

          {alertsPaused ? (
            <PrimaryButton
              type="button"
              onClick={() => setConfirmAction("activate")}
              disabled={savingActive}
            >
              <ButtonLabel>Reactivate my profile</ButtonLabel>
            </PrimaryButton>
          ) : (
            <DangerButton
              type="button"
              onClick={() => setConfirmAction("deactivate")}
              disabled={savingActive}
            >
              <ButtonLabel>Deactivate my profile</ButtonLabel>
            </DangerButton>
          )}
        </Container>
      </Container>

      {confirmAction && (
        <ModalOverlay onClick={() => !savingActive && setConfirmAction(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"22px"}
              fontWeight={500}
              lineHeight={"30px"}
              color="#202020"
            >
              {confirmAction === "deactivate" ? "Deactivate your profile?" : "Reactivate your profile?"}
            </Typography>
            <Typography
              tag="p"
              fontFamily="Neue Montreal"
              fontSize={"15px"}
              fontWeight={400}
              lineHeight={"22px"}
              color="#5c5c5c"
            >
              {confirmAction === "deactivate"
                ? "We will stop sending announcement alerts on WhatsApp. Your watchlist, plan and expiry date do not change, and you can reactivate from this page whenever you want."
                : "We will start sending announcement alerts again for the stocks on your watchlist."}
            </Typography>
            <Container gap="12px" justifyContent="flex-end" wrap>
              <SecondaryButton
                type="button"
                onClick={() => setConfirmAction(null)}
                disabled={savingActive}
              >
                <ButtonLabel color="#202020">Cancel</ButtonLabel>
              </SecondaryButton>
              {confirmAction === "deactivate" ? (
                <DangerButton
                  type="button"
                  onClick={() => applyActiveChange(false)}
                  disabled={savingActive}
                >
                  <ButtonLabel>{savingActive ? "Deactivating..." : "Deactivate"}</ButtonLabel>
                </DangerButton>
              ) : (
                <PrimaryButton
                  type="button"
                  onClick={() => applyActiveChange(true)}
                  disabled={savingActive}
                >
                  <ButtonLabel>{savingActive ? "Reactivating..." : "Reactivate"}</ButtonLabel>
                </PrimaryButton>
              )}
            </Container>
          </ModalCard>
        </ModalOverlay>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="light"
        transition={Bounce}
        aria-label="toast"
      />
    </>
  );
};

const StatusChip = styled.span<{ $paused?: boolean }>`
  background: ${({ $paused }) => ($paused ? "#e9ecef" : "#198754")};
  color: ${({ $paused }) => ($paused ? "#6c757d" : "#fff")};
  padding: 4px 12px;
  border-radius: 12px;
  font-family: "Neue Montreal", sans-serif;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`;

const BaseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  border-radius: 100px;
  padding: 10px 24px;
  width: fit-content;
  font: inherit;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const DangerButton = styled(BaseButton)`
  background: #f10303;
`;

const PrimaryButton = styled(BaseButton)`
  background: linear-gradient(165.56deg, #5395ff 39.75%, #d69dff 58.69%);
`;

const SecondaryButton = styled(BaseButton)`
  background: #f1f1f1;
`;

const ButtonLabel = styled.span<{ color?: string }>`
  font-family: "Neue Montreal", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 19.6px;
  color: ${({ color }) => color || "#fff"};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(32, 32, 32, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const ModalCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  width: min(100%, 460px);
`;

export default Profile; 